/*
 * Copyright (c) Akveo 2019. All Rights Reserved.
 * Licensed under the Single Application / Multi Application License.
 * See LICENSE_SINGLE_APP / LICENSE_MULTI_APP in the 'docs' folder for license information on type of purchased license.
 */

import { Inject, Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpErrorResponse
} from '@angular/common/http';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, finalize, switchMap, take } from 'rxjs/operators';
import { NbAuthResult, NbAuthService, NbAuthToken, NB_AUTH_TOKEN_INTERCEPTOR_FILTER } from '@nebular/auth';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {

  refreshTokenInProgress = false; // is a token being currently refreshed
  tokenSubject = new BehaviorSubject<string>(null); // kind of like an event handler

  constructor(
    private router: Router,
    private injector: Injector,
    @Inject(NB_AUTH_TOKEN_INTERCEPTOR_FILTER) protected filter
    ) { }



  // handles returned errors from HTTP requests
  private handleAuthError(error: HttpErrorResponse): Observable<any> {
    if (error.status === 401 && !error.url.includes('auth/login')) {
      this.router.navigate(['auth/login']);
      setTimeout(() => {
        window.location.reload();
      }, 100);
      return throwError(error);
    }
    // 403 means refresh token has failed
    if (error.status === 403 && error.url.includes('users/current')) {
      // at login page
      return throwError(error);
    } else if (error.status === 403 && !error.url.includes('auth/login')) {
      this.router.navigate(['auth/login']);
      if (window.location.pathname === '/auth/login') {
        // already at login
        return throwError(error);
      } else {
        setTimeout(() => {
          window.location.reload();
        }, 100);
      }
      return of(error.message);
    } else {
      return throwError(error);
    }
  }

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    // do not intercept request whose urls are filtered by the injected filter
    if (!this.filter(req)) {
      // Returns true if auth token is present in the token storage 
      // (only checks there is a access_token in the object) no fancy checks or validity done here
      return this.authService.isAuthenticated()
        .pipe(
          switchMap(authenticated => {
            if (authenticated) {
              // if there is a token send it!
              return this.authService.getToken().pipe(
                switchMap((token: NbAuthToken) => {
                  const JWT = `Bearer ${token.getValue()}`;
                  req = req.clone({
                    setHeaders: {
                      Authorization: JWT,
                    },
                  });
                  return next.handle(req).pipe(catchError(x => this.handleAuthError(x)));
                }),
              )
            } else {
              // nebular thinks the access token is missing (if we have had a 401 then the access token will have been removed from localStorage)
              // so lets refresh if possible (i.e. refresh token has not expired also)
              if (!this.refreshTokenInProgress) {
                this.refreshTokenInProgress = true;
                this.tokenSubject.next(null);
                // this bit seems pointless but we need an empty token object (NbAuthToken) to get the OwnerStrategyName
                return this.authService.getToken().pipe(
                  switchMap((token: NbAuthToken) => {
                    if (token.getValue() && !token.isValid()) {
                      // of course its not valid, we wouldn't be here otherwise!
                      // the OwnerStrategyName gets the URL for the refresh 
                      //Sends a refresh token request Stores received token in the token storage
                      // Example: refreshToken('email', {token: token})
                      return this.authService.refreshToken(token.getOwnerStrategyName(), token)
                        .pipe(
                          switchMap((newToken: NbAuthResult) => {
                            if (newToken.isSuccess()) {
                              this.tokenSubject.next(newToken.getToken().getValue());
                              const JWT = `Bearer ${newToken.getToken().getValue()}`;
                              req = req.clone({
                                setHeaders: {
                                  Authorization: JWT,
                                },
                              });
                              return next.handle(req).pipe(catchError(x => this.handleAuthError(x)));
                            } else {
                              // refresh token failed go to login
                              localStorage.removeItem('auth_app_token');
                              //THIS SHOULD DO THE SAME BUT WOULD NOT WORK -->> this.authService.logout(token.getOwnerStrategyName()); // error on JWT logout
                              return next.handle(req).pipe(catchError(x => this.handleAuthError(x)));
                            }
                          }),
                          catchError((error) => {
                            // refresh token failed go to login
                            localStorage.removeItem('auth_app_token');
                            //THIS SHOULD DO THE SAME BUT WOULD NOT WORK -->> this.authService.logout(token.getOwnerStrategyName()); // error on JWT logout
                            return next.handle(req).pipe(catchError(x => this.handleAuthError(x)));
                          }),
                          finalize(() => {
                            this.refreshTokenInProgress = false; // all done here
                          })
                        )
                    } else {
                      // if we get here then we didn't need to try and get a refresh token so set refreshTokenInProgress to false and carry on
                      this.refreshTokenInProgress = false;
                      return next.handle(req).pipe(catchError(x => this.handleAuthError(x)));
                    }
                  }));
              } else {
                // line up handles to wait for new token
                return this.tokenSubject.pipe(
                  this.filter(token => token != null), // filters wait for something to change
                  take(1), // take the first emmision only
                  switchMap(token => {
                    const JWT = `Bearer ${token}`;
                    req = req.clone({
                      setHeaders: {
                        Authorization: JWT,
                      },
                    });
                    return next.handle(req).pipe(catchError(x => this.handleAuthError(x)));
                  })
                )
              }
            }
          }),
        )
    } else {
      // filtered request so dont worry about tokens and appending to request just fire away
      // these are likely login ones or those with out authenication called when app is initilisaed 
      return next.handle(req).pipe(catchError(x => this.handleAuthError(x)));
    }
  }

  protected get authService(): NbAuthService {
    return this.injector.get(NbAuthService);
  }

}
